import { useQuery } from '@apollo/client';
import { Plus, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerFormDialog } from '@/modules/customers-pets/CustomerFormDialog';
import { CUSTOMERS_QUERY, CUSTOMER_QUERY } from '@/modules/customers-pets/customers.gql';
import { PetFormDialog } from '@/modules/customers-pets/PetFormDialog';
import { PETS_QUERY } from '@/modules/customers-pets/pets.gql';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

import { PetQrScanBox, type PetQrHit } from './PetQrScanBox';

interface CustomerLite {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string;
}

interface PetLite {
  id: string;
  customer_id: string;
  name: string;
  species: string | null;
  is_deceased: number;
  status: 'active' | 'inactive';
}

interface CustomersPage {
  customers: { items: CustomerLite[]; page: { total: number } };
}
interface PetsPage {
  pets: { items: PetLite[]; page: { total: number } };
}

const PET_SEARCH_VISIBLE_THRESHOLD = 8;

interface CustomerPetPickerProps {
  customer_id: string | null;
  pet_id: string | null;
  onChange: (next: { customer_id: string | null; pet_id: string | null }) => void;
  disabled?: boolean;
}

export function CustomerPetPicker({
  customer_id,
  pet_id,
  onChange,
  disabled,
}: CustomerPetPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pet_search, setPetSearch] = useState('');
  const [pet_debounced, setPetDebounced] = useState('');
  const [show_create_customer, setShowCreateCustomer] = useState(false);
  const [show_create_pet, setShowCreatePet] = useState(false);
  // Whether to show the pet search box must depend on the customer's TOTAL pet count,
  // not the current (filtered) result count — otherwise narrowing the search below the
  // threshold hides the very input the user just typed into, trapping them.
  const [total_pets_unfiltered, setTotalPetsUnfiltered] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const handle = window.setTimeout(() => setPetDebounced(pet_search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [pet_search]);

  useEffect(() => {
    setPetSearch('');
    setTotalPetsUnfiltered(0);
  }, [customer_id]);

  const { data: customers_data, loading: customers_loading } = useQuery<CustomersPage>(
    CUSTOMERS_QUERY,
    {
      variables: { offset: 0, limit: 20, search: debounced || null, status_filter: 'ACTIVE' },
      skip: !!customer_id || debounced.length < 2,
    },
  );

  const { data: customer_record } = useQuery<{ customer: CustomerLite }>(CUSTOMER_QUERY, {
    variables: { id: customer_id ?? '' },
    skip: !customer_id,
  });

  const { data: pets_data, refetch: refetchPets } = useQuery<PetsPage>(PETS_QUERY, {
    variables: {
      offset: 0,
      limit: 100,
      search: pet_debounced || null,
      customer_id: customer_id ?? null,
      include_deceased: false,
      status_filter: 'ACTIVE',
    },
    skip: !customer_id,
  });

  useEffect(() => {
    if (!pet_debounced && pets_data?.pets.page.total !== undefined) {
      setTotalPetsUnfiltered(pets_data.pets.page.total);
    }
  }, [pet_debounced, pets_data?.pets.page.total]);

  const selected_customer = customers_data?.customers.items.find((c) => c.id === customer_id)
    ?? customer_record?.customer
    ?? null;

  const onClickClearCustomer = (): void => {
    onChange({ customer_id: null, pet_id: null });
    setSearch('');
  };

  const onClickPickCustomer = (c: CustomerLite): void => {
    onChange({ customer_id: c.id, pet_id: null });
  };

  const onClickPickPet = (p: PetLite): void => {
    if (!customer_id) return;
    onChange({ customer_id, pet_id: p.id });
  };

  const onResolvePetQr = (hit: PetQrHit): void => {
    onChange({ customer_id: hit.customer_id, pet_id: hit.pet_id });
  };

  const selected_pet = pets_data?.pets.items.find((p) => p.id === pet_id) ?? null;

  return (
    <div className="space-y-3">
      {customer_id && selected_customer ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 p-3">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">
              {selected_customer.first_name}
              {selected_customer.last_name ? ` ${selected_customer.last_name}` : ''}
            </p>
            <p className="font-mono text-xs text-muted-foreground">{selected_customer.phone}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClickClearCustomer}
            disabled={disabled}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <PetQrScanBox on_resolve={onResolvePetQr} disabled={disabled} />
          <Input
            type="search"
            placeholder={t('doctor_pos.customer_search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />
          {debounced.length >= 2 ? (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-md p-1">
              {customers_loading ? (
                <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
              ) : null}
              {customers_data?.customers.items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onClickPickCustomer(c)}
                  disabled={disabled}
                  className="flex w-full items-center justify-between rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border hover:bg-secondary"
                >
                  <span>
                    {c.first_name}
                    {c.last_name ? ` ${c.last_name}` : ''}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{c.phone}</span>
                </button>
              ))}
              {(customers_data?.customers.items.length ?? 0) === 0 && !customers_loading ? (
                <p className="text-xs text-muted-foreground">{t('doctor_pos.no_customer_match')}</p>
              ) : null}
            </div>
          ) : null}
          {customers_data && customers_data.customers.page.total > customers_data.customers.items.length ? (
            <p className="text-xs text-muted-foreground">
              {t('doctor_pos.customer_showing_count', {
                shown: customers_data.customers.items.length,
                total: customers_data.customers.page.total,
              })}
            </p>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowCreateCustomer(true)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('customers.create')}</span>
          </Button>
        </div>
      )}

      {customer_id ? (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('doctor_pos.pet_label')}
          </p>
          {total_pets_unfiltered > PET_SEARCH_VISIBLE_THRESHOLD ? (
            <Input
              type="search"
              placeholder={t('doctor_pos.pet_search_placeholder')}
              value={pet_search}
              onChange={(e) => setPetSearch(e.target.value)}
              disabled={disabled}
              className="h-8 text-sm"
            />
          ) : null}
          <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto rounded-md p-1">
            {pets_data?.pets.items.map((p) => (
              <Button
                key={p.id}
                type="button"
                size="sm"
                variant={p.id === pet_id ? 'default' : 'outline'}
                onClick={() => onClickPickPet(p)}
                disabled={disabled}
              >
                {p.name}
                {p.species ? <Badge variant="muted" className="ml-2">{p.species}</Badge> : null}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowCreatePet(true)}
              disabled={disabled}
            >
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('pets.create')}</span>
            </Button>
          </div>
          {pets_data && pets_data.pets.page.total > pets_data.pets.items.length ? (
            <p className="text-xs text-muted-foreground">
              {t('doctor_pos.pet_showing_count', {
                shown: pets_data.pets.items.length,
                total: pets_data.pets.page.total,
              })}
            </p>
          ) : null}
          {selected_pet?.is_deceased === 1 ? (
            <Badge variant="muted">{t('pets.deceased')}</Badge>
          ) : null}
        </div>
      ) : null}

      {show_create_customer ? (
        <CustomerFormDialog
          mode="create"
          onClose={() => setShowCreateCustomer(false)}
          onSaved={(id) => {
            setShowCreateCustomer(false);
            onChange({ customer_id: id, pet_id: null });
          }}
        />
      ) : null}
      {show_create_pet && customer_id ? (
        <PetFormDialog
          mode="create"
          customer_id={customer_id}
          onClose={() => setShowCreatePet(false)}
          onSaved={async (new_pet_id) => {
            setShowCreatePet(false);
            await refetchPets();
            if (new_pet_id) {
              onChange({ customer_id, pet_id: new_pet_id });
            }
          }}
        />
      ) : null}
    </div>
  );
}
